import React from "react";
import { Plural } from "@lingui/macro";
import { CruiseItineraryFragment } from "types/graphql-types";
import CruiseExcursionsList from "./CruiseExcursionsList";
import { OpenExcursionModal } from "./CruiseItinerary";
import { CategorisedShorex, ShorexCategory } from "./types.d";
import { RemoteLandProgramme } from "./types";

import * as styles from "./CruiseItineraryItemDetails.module.scss";
import TextBox from "components/TextBox/TextBox";

export interface CruiseItineraryItemDetailsProps {
  openExcursionModal: OpenExcursionModal;
  item: CruiseItineraryFragment;
  shorexes: CategorisedShorex[];
  landProgrammes: RemoteLandProgramme[];
  hideShorexes: boolean;
}

const CruiseItineraryItemDetails: React.FC<CruiseItineraryItemDetailsProps> = ({
  item,
  shorexes,
  landProgrammes,
  openExcursionModal,
  hideShorexes
}) => {
  const shorexModels = shorexes.map((entity) => ({
    name: entity.name,
    features: entity.features,
    accessibility: entity.accessibility,
    activityLevel: entity.activityLevel,
    category: entity.displayCategory,
    hideDurationAndCode: entity.hideDurationAndCode,
    code: entity.code,
    openModal: () => {
      if (entity.shorexId)
        openExcursionModal({
          type: "shorex",
          excursionId: entity.shorexId,
          duration: entity.duration,
          hideDurationAndCode: entity.hideDurationAndCode
        });
    }
  }));

  const landProgrammeModels = landProgrammes.map((entity) => ({
    name: entity.name,
    code: entity.code,
    openModal: () => {
      if (entity.landProgrammeId)
        openExcursionModal({
          type: "landProgramme",
          excursionId: entity.landProgrammeId,
          duration: undefined,
          hideDurationAndCode: false
        });
    }
  }));

  const inclusiveExcursionModels = shorexModels.filter(
    (x) => x.category === ShorexCategory.IncludedInAllInclusive
  );
  const premiumExcursionModels = shorexModels.filter(
    (x) => x.category === ShorexCategory.Premium
  );
  const midVoyageLandAdventureModels = shorexModels.filter(
    (x) => x.category === ShorexCategory.MidVoyageLandAdventure
  );

  const shorexesList = [
    {
      title: (
        <Plural
          value={inclusiveExcursionModels.length || 0}
          one="# Included Shore Excursion"
          other="# Included Shore Excursions"
        />
      ),
      items: inclusiveExcursionModels
    },
    {
      title: (
        <Plural
          value={premiumExcursionModels.length || 0}
          one="# Premium Shore Excursion"
          other="# Premium Shore Excursions"
        />
      ),
      items: premiumExcursionModels
    },
    {
      title: (
        <Plural
          value={midVoyageLandAdventureModels.length || 0}
          one="# Mid-Voyage Land Adventure"
          other="# Mid-Voyage Land Adventures"
        />
      ),
      items: midVoyageLandAdventureModels
    }
  ];

  const landProgrammesList = [
    {
      title: (
        <Plural
          value={landProgrammes?.length || 0}
          one="# Land Programme"
          other="# Land Programmes"
        />
      ),
      items: landProgrammeModels
    }
  ];

  const excursions = [
    ...(hideShorexes ? [] : shorexesList),
    ...(hideShorexes ? [] : landProgrammesList)
  ];

  return (
    <React.Fragment>
      <div className={styles.container}>
        {item?.port?.shortDescriptionOverride?.localized ? (
          <TextBox
            fontSizeResponsive
            fontSize="medium"
            content={item?.port?.shortDescriptionOverride?.localized}
          />
        ) : (
          <>{item?.port?.data?.shortDescription}</>
        )}
      </div>
      <CruiseExcursionsList excursions={excursions} />
    </React.Fragment>
  );
};

export default CruiseItineraryItemDetails;
